import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  ApprovalBizTypeRow,
  ApprovalDelegateRow,
  ApprovalFlowAuthRow,
  ApprovalFlowAuthSaveRequest,
  ApprovalFlowDetail,
  ApprovalFlowRow,
  ApprovalFlowSaveRequest,
  ApprovalVariableRow
} from '../types/approvalFlow';

/** 审批流配置：流程 / 节点 / 条件路由 / 变量 / 委托（需 sys:approval:manage 或管理员）。 */
export function createApprovalFlowApi(http: HttpClient) {
  return {
    flow: {
      list(query: { bizType?: string; status?: string; keyword?: string } = {}): Promise<ApprovalFlowRow[]> {
        return http.request<ApprovalFlowRow[]>({ path: '/sys/approval/flow', query: toQuery(query) });
      },
      detail(id: Id): Promise<ApprovalFlowDetail> {
        return http.request<ApprovalFlowDetail>({ path: `/sys/approval/flow/${id}` });
      },
      create(body: ApprovalFlowSaveRequest): Promise<{ id: string; flowCode: string; version: number; status: string }> {
        return http.request<{ id: string; flowCode: string; version: number; status: string }>({
          path: '/sys/approval/flow',
          method: 'POST',
          body
        });
      },
      update(
        id: Id,
        body: ApprovalFlowSaveRequest
      ): Promise<{ id: string; flowCode: string; version: number; status: string }> {
        return http.request<{ id: string; flowCode: string; version: number; status: string }>({
          path: `/sys/approval/flow/${id}`,
          method: 'PUT',
          body
        });
      },
      publish(id: Id): Promise<{ id: string; status: string; version: number }> {
        return http.request<{ id: string; status: string; version: number }>({
          path: `/sys/approval/flow/${id}/publish`,
          method: 'POST'
        });
      },
      disable(id: Id): Promise<{ id: string; status: string }> {
        return http.request<{ id: string; status: string }>({
          path: `/sys/approval/flow/${id}/disable`,
          method: 'POST'
        });
      },
      remove(id: Id): Promise<{ id: string; deleted: boolean }> {
        return http.request<{ id: string; deleted: boolean }>({
          path: `/sys/approval/flow/${id}`,
          method: 'DELETE'
        });
      }
    },

    /** 独立授权层：START 发起 / MANAGE 管理 / VIEW_LOG 查轨迹 / INTERVENE 干预；未配置则放行。 */
    auth: {
      list(flowId: Id): Promise<ApprovalFlowAuthRow[]> {
        return http.request<ApprovalFlowAuthRow[]>({ path: `/sys/approval/flow/${flowId}/auth` });
      },
      save(flowId: Id, body: ApprovalFlowAuthSaveRequest): Promise<{ id: string }> {
        return http.request<{ id: string }>({
          path: `/sys/approval/flow/${flowId}/auth`,
          method: 'POST',
          body
        });
      },
      update(authId: Id, body: ApprovalFlowAuthSaveRequest): Promise<{ id: string; status: string }> {
        return http.request<{ id: string; status: string }>({
          path: `/sys/approval/flow/auth/${authId}`,
          method: 'PUT',
          body
        });
      },
      remove(authId: Id): Promise<{ id: string; deleted: boolean }> {
        return http.request<{ id: string; deleted: boolean }>({
          path: `/sys/approval/flow/auth/${authId}`,
          method: 'DELETE'
        });
      }
    },

    bizTypes(): Promise<ApprovalBizTypeRow[]> {
      return http.request<ApprovalBizTypeRow[]>({ path: '/sys/approval/biz-type' });
    },

    variables: {
      list(bizType?: string): Promise<ApprovalVariableRow[]> {
        return http.request<ApprovalVariableRow[]>({
          path: '/sys/approval/variable',
          query: toQuery({ bizType })
        });
      },
      save(body: {
        varCode: string;
        varName?: string;
        varType?: string;
        sourceType?: string;
        sourceExpr?: string;
        bizTypes?: string;
        unit?: string;
        remark?: string;
      }): Promise<{ varCode: string }> {
        return http.request<{ varCode: string }>({ path: '/sys/approval/variable', method: 'POST', body });
      },
      remove(id: Id): Promise<{ id: string; deleted: boolean }> {
        return http.request<{ id: string; deleted: boolean }>({
          path: `/sys/approval/variable/${id}`,
          method: 'DELETE'
        });
      }
    },

    delegates: {
      list(): Promise<ApprovalDelegateRow[]> {
        return http.request<ApprovalDelegateRow[]>({ path: '/sys/approval/delegate' });
      },
      save(body: {
        principalUserId: string;
        agentUserId: string;
        bizTypes?: string;
        flowIds?: string;
        delegateType?: string;
        autoForward?: boolean;
        beginAt: string;
        endAt: string;
      }): Promise<{ id: string }> {
        return http.request<{ id: string }>({ path: '/sys/approval/delegate', method: 'POST', body });
      },
      revoke(id: Id): Promise<{ id: string; status: string }> {
        return http.request<{ id: string; status: string }>({
          path: `/sys/approval/delegate/${id}/revoke`,
          method: 'POST'
        });
      }
    }
  };
}

export type ApprovalFlowApi = ReturnType<typeof createApprovalFlowApi>;
